// Central handling of 401 and 403 for every call to the LUD API.
//
// The app's API helpers each call `fetch` themselves, so this wraps `window.fetch` ONCE (idempotent) and only
// observes the answers of requests that go to the LUD API with a bearer token:
//
//   401  the session is not accepted   -> onUnauthorized()  (the context confirms with /me and signs out only if the
//                                         server really rejects the token; repeated 401s are collapsed)
//   403  the user is signed in but not -> onForbidden()      (a localized notice and an access refresh; NEVER a sign-out
//        allowed to do this                                   and never a redirect)
//
// Excluded on purpose: the sign-in / password-reset calls (their 401 means "wrong credentials") and `/me`
// (the context handles its answer itself, with its own retry rules). The wrapped fetch returns the original response
// untouched, so every caller keeps its own handling.

import config from "../../config.json";

const API_BASE = String(config.BASE_AUTH_API || "");
// Calls whose 401 / 403 are part of their normal protocol.
const IGNORED = [/^\/?me(\?|$)/, /^\/?signin(\?|$)/, /^\/?signup(\?|$)/, /^\/?forgot-password(\?|$)/, /^\/?verify-reset-code(\?|$)/, /^\/?reset-password(\?|$)/];
// One signal per burst: pages fire several requests at once.
const SIGNAL_WINDOW_MS = 2000;

let installed = null;

const urlOf = (input) => (typeof input === "string" ? input : input && input.url) || "";

const bearerOf = (input, init) => {
  const fromInit = init && init.headers;
  const read = (headers) => {
    if (!headers) return "";
    if (typeof headers.get === "function") return headers.get("Authorization") || headers.get("authorization") || "";
    return headers.Authorization || headers.authorization || "";
  };
  return read(fromInit) || read(input && typeof input === "object" ? input.headers : null);
};

export const isGuardedRequest = (input, init) => {
  const url = urlOf(input);
  if (!API_BASE || !url.startsWith(API_BASE)) return false;
  if (!/^Bearer\s+\S+/.test(bearerOf(input, init))) return false;
  const path = url.slice(API_BASE.length);
  return !IGNORED.some((pattern) => pattern.test(path));
};

export function installApiResponseGuard({ onUnauthorized, onForbidden } = {}) {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return () => {};
  // Re-installing replaces the handlers; the wrapper is created once.
  if (installed) {
    installed.state.handlers = { onUnauthorized, onForbidden };
    return installed.uninstall;
  }

  const original = window.fetch;
  const state = { handlers: { onUnauthorized, onForbidden }, lastUnauthorized: 0, lastForbidden: 0 };

  const wrapped = async function guardedFetch(input, init) {
    const response = await original.call(this, input, init);
    try {
      if (response && (response.status === 401 || response.status === 403) && isGuardedRequest(input, init)) {
        const now = Date.now();
        if (response.status === 401) {
          // A 401 for a token that is already gone (signed out meanwhile) is nothing to act on.
          const hasToken = (() => { try { return Boolean(localStorage.getItem("auth-token")); } catch { return false; } })();
          if (hasToken && now - state.lastUnauthorized > SIGNAL_WINDOW_MS) {
            state.lastUnauthorized = now;
            state.handlers.onUnauthorized && state.handlers.onUnauthorized({ url: urlOf(input), status: 401 });
          }
        } else if (now - state.lastForbidden > SIGNAL_WINDOW_MS) {
          state.lastForbidden = now;
          state.handlers.onForbidden && state.handlers.onForbidden({ url: urlOf(input), status: 403 });
        }
      }
    } catch {
      // observing must never break a request
    }
    return response;
  };
  wrapped.__ludGuard = true;
  window.fetch = wrapped;

  const uninstall = () => {
    if (window.fetch === wrapped) window.fetch = original;
    installed = null;
  };
  installed = { uninstall, state };
  return uninstall;
}
